export const siteStyles = [
  {
    id: 'editorial',
    name: 'Warm editorial',
    colors: ['#f5efe5', '#3d4535', '#bb9364'],
    surface: '#e9e4d8',
    muted: '#626656',
    font: 'Georgia,"Times New Roman",serif',
  },
  {
    id: 'luxury',
    name: 'Rose atelier',
    colors: ['#fff5f3', '#422d33', '#b77583'],
    surface: '#f4e4e2',
    muted: '#765c65',
    font: 'Georgia,"Times New Roman",serif',
  },
] as const;
export type SiteAppearance = {
  style?: (typeof siteStyles)[number]['id'] | 'studio' | 'signature';
  palette?: SitePalette;
  spacing?: 'Airy' | 'Balanced' | 'Compact';
};

export type SitePalette = {
  background: string;
  primary: string;
  accent: string;
};
const legacyStyle = {
  id: 'studio',
  name: 'Modern studio',
  colors: ['#f5f3ff', '#26213c', '#9b87ca'],
  surface: '#eae5f5',
  muted: '#6c6281',
  font: '"Helvetica Neue",Arial,sans-serif',
};
const hex = /^#[0-9a-f]{6}$/i;
export function appearanceStyle(appearance: SiteAppearance) {
  if (appearance.style === 'studio') return legacyStyle;
  if (appearance.style !== 'signature')
    return siteStyles.find((item) => item.id === appearance.style);
  const palette = appearance.palette;
  if (!palette || !Object.values(palette).every((color) => hex.test(color)))
    return undefined;
  const rgb = [1, 3, 5]
    .map((i) => Number.parseInt(palette.background.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  const luminance = rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722;
  const ink = luminance > 0.179 ? '#000000' : '#ffffff';
  return {
    id: 'signature',
    name: 'Your look',
    colors: [palette.background, ink, palette.primary],
    surface: `#${[1, 3, 5]
      .map((index) =>
        Math.round(
          Number.parseInt(palette.accent.slice(index, index + 2), 16) * 0.1 +
            Number.parseInt(palette.background.slice(index, index + 2), 16) *
              0.9
        )
          .toString(16)
          .padStart(2, '0')
      )
      .join('')}`,
    muted: ink,
    font: 'inherit',
  };
}

/** Fixed CSS only: preserve the generated markup, original photos and sandbox. */
export function appearancePreview(
  html: string,
  appearance: SiteAppearance | null
) {
  if (!appearance) return html;
  const style = appearanceStyle(appearance);
  const spacing = appearance.spacing
    ? {
        Airy: 'clamp(4rem,9vw,8rem)',
        Balanced: 'clamp(3rem,6vw,5rem)',
        Compact: 'clamp(2rem,4vw,3rem)',
      }[appearance.spacing]
    : undefined;
  let css = '';
  if (style) {
    const [background, ink, accent] = style.colors;
    css += `:root{--pearl:${background};--pearl2:${style.surface};--rose:${accent};--rosed:${ink};--esp:${ink};--mut:${style.muted};--bg:${background};--background:${background};--surface:${style.surface};--ink:${ink};--text:${ink};--accent:${accent};--primary:${ink};--muted:${style.muted};--color-background:${background};--color-text:${ink};--color-accent:${accent}}body{background:${background}!important;color:${ink}!important}${style.font === 'inherit' ? '' : `h1,h2,h3{font-family:${style.font}!important}`}header,.top{background:${background}!important}em{color:${accent}!important}.btn,button,a[class*="button"],a[class*="cta"]{background:${ink}!important;color:${background}!important}.flist li{background:${style.surface}!important}.flist li:nth-child(even){background:${style.surface}!important}.about{background:${style.surface}!important}.visit{background:${ink}!important;color:${background}!important}.card{background:${background}!important}`;
  }
  if (spacing)
    css += `main>section,main>article,main>.feat,main>.menu,main>.about,main>.visit{padding-top:${spacing}!important;padding-bottom:${spacing}!important}`;
  const sheet = `<style id="borradh-appearance-preview">${css}</style>`;
  return /<\/head>/i.test(html)
    ? html.replace(/<\/head>/i, `${sheet}</head>`)
    : `${sheet}${html}`;
}
export function appearancePrompt(appearance: SiteAppearance) {
  const style = appearanceStyle(appearance);
  return `Save this appearance only: ${style ? `${style.name} palette: background ${style.colors[0]}, text ${style.colors[1]}, accent ${style.colors[2]}, secondary surfaces ${style.surface}, muted text ${style.muted}; heading font ${style.font === 'inherit' ? 'preserve the existing font' : style.font}.` : 'Keep existing colours and typography.'} ${appearance.spacing ? `Use ${appearance.spacing.toLowerCase()} section spacing.` : 'Keep existing spacing.'} Preserve the exact existing layout, all photo cropping and image URLs, copy, prices and booking links. CSS only, respecting reduced motion.`;
}
